import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import ImageIcon from '@mui/icons-material/ImageOutlined';

export const PLACEHOLDER_IMAGE_URL = `${process.env.PUBLIC_URL || ''}/placeholder_image.png`;

// Shared crop for list media: full-height cover, anchored at the top.
export const MEDIA_OBJECT_POSITION = 'center top';

// Normalizes image sources into an ordered, de-duplicated list of
// { url, alt }, highest priority first (e.g. experience photo, then the
// item's default image). Empty entries are dropped.
export const normalizeSources = sources => {
    const seen = new Set();
    return (sources || [])
        .filter(s => s && s.url)
        .filter(s => {
            if (seen.has(s.url)) return false;
            seen.add(s.url);
            return true;
        })
        .map(s => ({ url: s.url, alt: s.alt || '' }));
}

// First source that hasn't failed; null means fall back to the placeholder.
export const pickSource = (sources, failed) => sources.find(s => !failed.has(s.url)) || null;

const imgSx = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: MEDIA_OBJECT_POSITION,
    display: 'block',
};

// Left media region for cards. Tries each source in order, then the local
// placeholder, and finally a themed icon if even the placeholder fails, so
// the column never collapses or shows a broken image.
export const CardMedia = ({ sources, resetKey, onSourceFailed }) => {
    const list = useMemo(() => normalizeSources(sources), [sources]);
    const [failed, setFailed] = useState(() => new Set());
    const [placeholderFailed, setPlaceholderFailed] = useState(false);
    const lastResetKey = useRef(resetKey);

    // A different item starts with a clean slate. Within the same item,
    // failures are kept only for URLs still in the sequence, so an added or
    // changed source loads while a known-broken one isn't retried.
    useEffect(() => {
        if (lastResetKey.current !== resetKey) {
            lastResetKey.current = resetKey;
            setFailed(new Set());
            setPlaceholderFailed(false);
            return;
        }
        setFailed(prev => {
            const urls = new Set(list.map(s => s.url));
            const next = new Set([...prev].filter(u => urls.has(u)));
            return next.size === prev.size ? prev : next;
        });
    }, [resetKey, list]);

    const current = pickSource(list, failed);

    const handleError = url => () => {
        setFailed(prev => new Set(prev).add(url));
        onSourceFailed?.(url);
    };

    return (
        <Box sx={{ position: 'relative', bgcolor: 'sage.surface', minHeight: '100%', overflow: 'hidden' }}>
            {current ?
                <Box component="img" key={current.url} src={current.url} alt={current.alt} loading="lazy" onError={handleError(current.url)} sx={imgSx} />
            : !placeholderFailed ?
                <Box
                    component="img"
                    src={PLACEHOLDER_IMAGE_URL}
                    alt=""
                    aria-hidden="true"
                    data-testid="card-media-placeholder"
                    onError={() => setPlaceholderFailed(true)}
                    sx={{ ...imgSx, opacity: theme => theme.palette.mode === 'dark' ? 0.45 : 1 }}
                />
            :
                <Box data-testid="card-media-fallback" aria-hidden="true" sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', pt: '24px', color: 'sage.main' }}>
                    <ImageIcon sx={{ fontSize: 36 }} />
                </Box>
            }
        </Box>
    );
}
